import shpwrite from '@mapbox/shp-write'
import { saveAs } from 'file-saver'

// WGS84 (EPSG:4326) – https://github.com/mapbox/shp-write#custom-prj-file, https://www.epsg.io/4326
const WGS84_PRJ = 'GEOGCS["WGS 84",DATUM["WGS_1984",SPHEROID["WGS 84",6378137,298.257223563,AUTHORITY["EPSG","7030"]],AUTHORITY["EPSG","6326"]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","4326"]]'

// DBF max 10 chars; skip duplicate keys after truncate
// (e.g. correctionStatus + correctionSource -> 2x "correction")
function toDbfProperties(properties) {
    const result = {}
    for (const key in properties) {
        const shortKey = key.slice(0, 10)
        if (shortKey in result) continue
        if (typeof properties[key] === 'object' && properties[key] !== null) continue
        result[shortKey] = properties[key]
    }
    return result
}

/**
 * Prepare the properties object for the shapefile DBF file.
 * ATTENTION: Keys for DBF file are limited to 10 characters.
 * @param {object} feature - Feature object with geometry and properties
 * @returns {object} - Properties object with vendor and optional metadata
 */
function prepareDbfProperties(feature) {
    const props = {
        ...(feature.properties || {})
    }

    // -------------------
    // Detect Trimble
    // -------------------
    const isTrimble =
        feature.crs?.properties?.name?.includes('trimble') ||
        feature.metadata?.common_projectId ||
        props.image?.includes('trimblegeospatial.com')

    if (isTrimble) {
        props.vendor = 'Trimble'

        // Optional metadata (short names for DBF)
        if (feature.metadata?.common_projectId) {
            props.proj_id = feature.metadata.common_projectId
        }

        if (feature.metadata?.common_averageHorizontal != null) {
            props.h_acc = feature.metadata.common_averageHorizontal
        }

        if (feature.metadata?.common_averageVertical != null) {
            props.v_acc = feature.metadata.common_averageVertical
        }

        if (feature.crs?.properties?.name) {
            props.crs_name = feature.crs.properties.name
        }
    }

    return toDbfProperties(props)
}

/**
 * Remove the Z coordinate from the geometry (shapefile is usually 2D).
 * @param {object} geometry - Geometry object with coordinates
 * @returns {object} - Geometry object with coordinates without Z
 */
function stripZFromGeometry(geometry) {
    if (!geometry || !geometry.coordinates) return geometry
    const { type, coordinates } = geometry

    if (type === 'Point') {
        return { type: 'Point', coordinates: coordinates.slice(0, 2) }
    }
    if (type === 'LineString') {
        return { type: 'LineString', coordinates: coordinates.map(c => c.slice(0, 2)) }
    }
    if (type === 'Polygon') {
        return { type: 'Polygon', coordinates: coordinates.map(ring => ring.map(c => c.slice(0, 2))) }
    }
    if (type === 'MultiPoint') {
        return { type: 'MultiPoint', coordinates: coordinates.map(c => c.slice(0, 2)) }
    }
    if (type === 'MultiLineString') {
        return { type: 'MultiLineString', coordinates: coordinates.map(line => line.map(c => c.slice(0, 2))) }
    }
    if (type === 'MultiPolygon') {
        return { type: 'MultiPolygon', coordinates: coordinates.map(poly => poly.map(ring => ring.map(c => c.slice(0, 2)))) }
    }
    return geometry
}

/**
 * Download a GeoJSON Feature (Point, LineString or Polygon) as a shapefile ZIP.
 * If there is an error, fallback to downloading GeoJSON.
 * @param {object} feature - Feature object with geometry and properties
 * @param {string} fileName - File name (without extension; .zip is added for shapefile)
 */
export async function downloadFeatureAsShapefile(feature, fileName) {
    const properties = prepareDbfProperties(feature)

    const normalized = {
        type: 'Feature',
        geometry: feature.geometry,
        properties: properties || {}
    }
    const geometry = stripZFromGeometry(normalized.geometry)
    const geomType = geometry?.type

    if (!geometry || !['Point', 'LineString', 'Polygon', 'MultiPoint', 'MultiLineString', 'MultiPolygon'].includes(geomType)) {
        console.error('Unsupported geometry type for shapefile:', geomType)
        await downloadFeatureAsGeoJSON(feature, (fileName || 'export').replace(/\.zip$/i, '') + '.geojson')
        return
    }

    const geoJson = {
        type: 'FeatureCollection',
        features: [{ type: 'Feature', geometry, properties: normalized.properties }]
    }

    const zipName = fileName.endsWith('.zip') ? fileName : `${fileName}.zip`

    try {
        const zipBlob = await shpwrite.zip(geoJson, {
            outputType: 'blob',
            prj: WGS84_PRJ
        })
        saveAs(zipBlob, zipName)
    } catch (error) {
        console.error('Error downloading shapefile:', error)
        await downloadFeatureAsGeoJSON(feature, (fileName || 'export').replace(/\.zip$/i, '') + '.geojson')
    }
}

/**
 * Download a feature as a GeoJSON file.
 * @param {object} feature - Feature object with geometry and properties
 * @param {string} fileName - File name (without extension; .geojson is added for GeoJSON)
 */
export function downloadFeatureAsGeoJSON(feature, fileName) {
    const normalized = {
        type: 'Feature',
        id: feature.id,
        geometry: feature.geometry,
        properties: feature.properties || {}
    }
    const name = fileName.endsWith('.geojson') || fileName.endsWith('.json') ? fileName : `${fileName}.geojson`
    const blob = new Blob([JSON.stringify(normalized, null, 2)], { type: 'application/geo+json' })
    saveAs(blob, name)
}
